import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HomeOutlined, MailOutlined, PhoneOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Input, InputNumber, message, Space } from 'antd';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import type { AgreementRow } from './AgreementsModal';
import { pickGoodLeapApplicationUrl } from './goodLeapPickUrl';
import {
    US_STATE_FALLBACK,
    buildStateOptionsFromMaster,
    resolveGoodLeapStateValue,
    GOODLEAP_CUSTOMER_PREFERENCE_OPTIONS,
} from './goodLeapShareFormOptions';
import './oneButton-css/GoodLeapShareLoanModal.css';
import './oneButton-css/OneButtonFormControls.css';
import { EnvelopeSimple, Handshake, House, Phone, User, X } from '@phosphor-icons/react';
import DashboardModal from './DashboardModal';
import { OBDSelect } from './form-elements';

type Phase = 'form' | 'iframe';

function splitSalesName(name: string | undefined): { first: string; last: string } {
    const n = (name || '').trim();
    if (!n) return { first: '', last: '' };
    const parts = n.split(/\s+/);
    if (parts.length === 1) return { first: parts[0], last: '' };
    return { first: parts[0], last: parts.slice(1).join(' ') };
}

function parseMoneyInput(raw: string | number | null | undefined): number | null {
    if (raw == null || raw === '') return null;
    const n = parseFloat(String(raw).replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? n : null;
}

function formatAddrStateSegment(state: string | undefined): string {
    const s = (state || '').trim();
    if (!s) return '';
    if (s.length <= 2) return s.toUpperCase();
    return s
        .toLowerCase()
        .split(/\s+/)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
}

function primaryCustomer(customers: unknown): Record<string, unknown> | null {
    if (!Array.isArray(customers)) return null;
    const hit = customers.find((c: unknown) => (c as Record<string, unknown>)?.IsPrimaryContact);
    return (hit as Record<string, unknown>) || (customers[0] as Record<string, unknown>) || null;
}

function primaryOpportunityCustomer(opportunityDetails: any): Record<string, unknown> | null {
    if (!opportunityDetails) return null;
    const od = opportunityDetails.OpportunityDetails ?? opportunityDetails;
    /** API often places `Customers` on the opportunity root (same as Angular `opportunityInformation`). */
    const list =
        opportunityDetails.Customers ??
        opportunityDetails.customers ??
        od?.Customers ??
        od?.customers;
    if (!Array.isArray(list)) return null;
    const hit = list.find((c: any) => c?.IsPrimaryContact);
    return (hit as Record<string, unknown>) || (list[0] as Record<string, unknown>) || null;
}

/**
 * Mirrors Angular `masterService.opportunityInformation` paths used in
 * `good-leap-share-loan-model.component.html` (OpportunityDetails, InstallationAddress, SalesPerson on one object).
 */
function opportunityDetailsRoot(raw: any): Record<string, unknown> | null {
    if (!raw || typeof raw !== 'object') return null;
    const o =
        raw.OpportunityDetails ??
        raw.opportunityDetails ??
        raw.Opportunityinfo?.OpportunityDetails ??
        raw.OpportunityInfo?.OpportunityDetails;
    return (o as Record<string, unknown>) || null;
}

function installationAddressRoot(raw: any): Record<string, unknown> | null {
    if (!raw || typeof raw !== 'object') return null;
    const a =
        raw.InstallationAddress ??
        raw.installationAddress ??
        raw.Opportunityinfo?.InstallationAddress ??
        raw.OpportunityInfo?.InstallationAddress;
    return (a as Record<string, unknown>) || null;
}

function salesPersonRoot(raw: any): Record<string, unknown> | null {
    if (!raw || typeof raw !== 'object') return null;
    const od = opportunityDetailsRoot(raw);
    const sp =
        raw.SalesPerson ??
        raw.salesPerson ??
        od?.SalesPerson ??
        od?.salesPerson ??
        raw.Opportunityinfo?.SalesPerson ??
        raw.OpportunityInfo?.SalesPerson;
    return (sp as Record<string, unknown>) || null;
}


export type GoodLeapShareLoanModalProps = {
    open: boolean;
    row: AgreementRow | null;
    opportunityId: string | number | null | undefined;
    opportunityDetails: any;
    statesFullName: { Value?: string; value?: string; Label?: string; label?: string }[] | undefined;
    theme: string;
    onClose: () => void;
    onCompleted: () => void;
};

/**
 * Trinity `GoodLeapShareLoanModelComponent`: loan share form → POST loanApplication → iframe on `link`
 * (or iframe immediately when `LoanStatus.Link` already exists).
 */
export const GoodLeapShareLoanModal: React.FC<GoodLeapShareLoanModalProps> = ({
    open,
    row,
    opportunityId,
    opportunityDetails,
    statesFullName,
    theme,
    onClose,
    onCompleted,
}) => {
    const [phase, setPhase] = useState<Phase>('form');
    const [iframeUrl, setIframeUrl] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [errText, setErrText] = useState('');

    const [offerId, setOfferId] = useState('');
    const [stateVal, setStateVal] = useState('');
    const [amountValue, setAmountValue] = useState<number | null>(null);
    const [applicantFName, setApplicantFName] = useState('');
    const [applicantLName, setApplicantLName] = useState('');
    const [applicantEmail, setApplicantEmail] = useState('');
    const [sendEmail, setSendEmail] = useState<'InPerson' | 'Email'>('InPerson');
    const [subFName, setSubFName] = useState('');
    const [subLName, setSubLName] = useState('');
    const [subEmail, setSubEmail] = useState('');
    const [firstYearProd, setFirstYearProd] = useState('');
    const [systemSize, setSystemSize] = useState('');
    const [batMfg, setBatMfg] = useState('');
    const [batModel, setBatModel] = useState('');
    const [batQty, setBatQty] = useState('');
    const [batAdder, setBatAdder] = useState('');
    const [batPrice, setBatPrice] = useState('');

    const initRowKeyRef = useRef<string>('');
    const applicantEmailTouchedRef = useRef(false);
    const subTouchedRef = useRef({ f: false, l: false, e: false });

    const resetFromRow = useCallback(() => {
        if (!row) return;
        const pre = pickGoodLeapApplicationUrl(row.loanStatus);
        if (pre) {
            setPhase('iframe');
            setIframeUrl(pre);
            setErrText('');
            return;
        }
        setPhase('form');
        setIframeUrl(null);
        setErrText('');

        const cust = primaryCustomer(row.goodLeapCustomers);
        const sp = salesPersonRoot(opportunityDetails);
        const od = opportunityDetailsRoot(opportunityDetails) ?? {};
        const addr = installationAddressRoot(opportunityDetails);
        const stRaw = String(
            od.State ??
                od.state ??
                (opportunityDetails as any)?.OpportunityDetails?.State ??
                (opportunityDetails as any)?.State ??
                (opportunityDetails as any)?.state ??
                addr?.State ??
                addr?.stateCode ??
                addr?.state ??
                '',
        ).trim();

        const masterOpts = buildStateOptionsFromMaster(statesFullName ?? []);
        const stateOpts = masterOpts.length ? masterOpts : US_STATE_FALLBACK;

        setOfferId(row.goodLeapOfferId || '');
        setStateVal(resolveGoodLeapStateValue(stRaw, stateOpts));
        const custFallback = cust || primaryOpportunityCustomer(opportunityDetails);
        setApplicantFName(String(custFallback?.FirstName ?? custFallback?.firstName ?? '').trim());
        setApplicantLName(String(custFallback?.LastName ?? custFallback?.lastName ?? '').trim());
        setApplicantEmail(String(custFallback?.Email ?? custFallback?.email ?? '').trim());

        // Match Angular `SalesPerson.Name.split(' ')[0/1]`
        const spFullName = String(sp?.Name ?? sp?.name ?? '').trim();
        const parts = spFullName ? spFullName.split(/\s+/) : [];
        setSubFName(parts[0] ?? '');
        setSubLName(parts[1] ?? '');
        setSubEmail(String(sp?.Email ?? sp?.email ?? '').trim());

        const fin = row.financedAmountRaw;
        const epc = row.totalEpcDisplay;
        const amtRaw = fin != null && String(fin).trim() !== '' ? String(fin) : epc || '';
        setAmountValue(parseMoneyInput(amtRaw));

        setSendEmail('InPerson');
        setFirstYearProd(String(row.annualProductionRaw ?? ''));
        setSystemSize(String(row.systemSizeRaw ?? ''));
        setBatMfg(String(row.batteryManufacturerNameRaw ?? ''));
        setBatModel(String(row.batteryModelNameRaw ?? ''));
        setBatQty(String(row.batteryQuantityRaw ?? ''));
        setBatAdder(String(row.batteryAdderSubTotalRaw ?? ''));
        setBatPrice(String(row.batteryPriceSubTotalRaw ?? ''));
    }, [row, opportunityDetails, statesFullName]);

    useEffect(() => {
        if (!open) {
            initRowKeyRef.current = '';
            applicantEmailTouchedRef.current = false;
            subTouchedRef.current = { f: false, l: false, e: false };
            return;
        }
        if (!row) return;
        if (initRowKeyRef.current === row.key) return;
        initRowKeyRef.current = row.key;
        resetFromRow();
    }, [open, row?.key, resetFromRow]);

    // Late-fill applicant fields when opportunityDetails arrives after modal opens.
    // Still matches Angular: values come from primary customer (IsPrimaryContact).
    useEffect(() => {
        if (!open || !row) return;
        const c = primaryOpportunityCustomer(opportunityDetails);
        if (!c) return;
        if (!applicantFName.trim()) setApplicantFName(String(c.FirstName ?? c.firstName ?? '').trim());
        if (!applicantLName.trim()) setApplicantLName(String(c.LastName ?? c.lastName ?? '').trim());
        if (!applicantEmailTouchedRef.current && !applicantEmail.trim()) {
            setApplicantEmail(String(c.Email ?? c.email ?? '').trim());
        }
    }, [open, row, opportunityDetails, applicantFName, applicantLName, applicantEmail]);

    const stateOptions = useMemo(() => {
        const fromMaster = buildStateOptionsFromMaster(statesFullName ?? []);
        return fromMaster.length ? fromMaster : US_STATE_FALLBACK;
    }, [statesFullName]);

    /** Use raw `ProductType` logic (Angular uses `params.data.ProductType`). */
    const ptTrim = String(row?.productTypeCode || row?.productType || '').trim();
    /** oneBUTTON: show battery block when product includes battery (e.g. SB, BR, BO). */
    const showBattery = ptTrim.toUpperCase().includes('B');
    /** Trinity `shouldShowField`: hide First Year Production + System Size when ProductType is `BO`. */
    const showSystemDesign = ptTrim.toUpperCase() !== 'BO';

    const handleSubmit = async () => {
        if (!row?.contractIdRaw || opportunityId == null || String(opportunityId).trim() === '') {
            message.error('Missing contract or opportunity id.');
            return;
        }
        if (!stateVal.trim()) {
            message.error('State is required.');
            return;
        }
        if (!applicantEmail.trim()) {
            message.error('Applicant email is required.');
            return;
        }
        setBusy(true);
        setErrText('');
        try {
            const isEmail = sendEmail === 'Email';
            const res = (await quoteServices.goodLeapLoanApplication({
                opportunityId,
                opportunityContractId: row.contractIdRaw,
                isEmail,
                email_Override: applicantEmail.trim(),
                submitting_User_First_Name: subFName.trim(),
                submitting_User_Last_Name: subLName.trim(),
                submitting_User_Email: subEmail.trim(),
            })) as Record<string, unknown>;

            const link = (res.link ?? res.Link) as string | undefined;
            const hasAppLink = typeof link === 'string' && /^https?:\/\//i.test(link);
            /** In-person: show iframe whenever API returns a link (Trinity then loads `LoanStatus.Link`). */
            if (!isEmail && hasAppLink) {
                setIframeUrl(link!.trim());
                setPhase('iframe');
                try {
                    await quoteServices.goodLeapGetLoanStatus(opportunityId, row.contractIdRaw);
                } catch {
                    // Non-blocking; agreements reload may still show stale status until refresh.
                }
                onCompleted();
                setBusy(false);
                return;
            }

            const isVerify = !!(res.isVerify ?? res.IsVerify);
            const verificationLink = (res.verificationLink ?? res.VerificationLink) as string | undefined;
            if (!isVerify) {
                if (verificationLink) window.open(verificationLink, '_blank', 'noopener,noreferrer');
                else setErrText(String(res.verificationStatus ?? res.VerificationStatus ?? 'Verification required.'));
                setBusy(false);
                return;
            }

            if (!isEmail) {
                if (!hasAppLink) {
                    setErrText(String(res.description ?? res.Description ?? 'No application link returned.'));
                }
            } else {
                if (link && /^https?:\/\//i.test(String(link))) {
                    message.success(
                        `The application has been submitted to ${String(opportunityDetails?.OpportunityDetails?.Email ?? applicantEmail)}.`,
                    );
                    try {
                        await quoteServices.goodLeapGetLoanStatus(opportunityId, row.contractIdRaw);
                    } catch {
                        // Non-blocking; agreements reload may still show stale status until refresh.
                    }
                    onCompleted();
                    onClose();
                } else {
                    setErrText(String(res.description ?? res.Description ?? 'Failed to send email.'));
                    message.error('Failed to send email');
                }
            }
        } catch (e: unknown) {
            const ax = e as { response?: { data?: { Message?: string } }; message?: string };
            const msg = ax?.response?.data?.Message ?? ax?.message ?? 'Loan application request failed.';
            setErrText(String(msg));
            message.error(String(msg));
        } finally {
            setBusy(false);
        }
    };

    const title =
        phase === 'iframe' ? 'Submit Application to Good Leap' : 'Submit Application to Good Leap';

    /** Sidebar: same fields as Angular `opportunityInformation` template, with API-shape fallbacks. */
    const glSidebar = useMemo(() => {
        const raw = opportunityDetails;
        if (!raw) {
            return {
                oppName: '',
                oppPhone: '',
                oppEmail: '',
                inst: null as Record<string, unknown> | null,
                sp: null as Record<string, unknown> | null,
            };
        }
        const od = opportunityDetailsRoot(raw);
        const primary = primaryOpportunityCustomer(raw);
        const inst = installationAddressRoot(raw);
        const sp = salesPersonRoot(raw);

        const oppName =
            String(od?.Name ?? od?.name ?? '').trim() ||
            [primary?.FirstName ?? primary?.firstName, primary?.LastName ?? primary?.lastName]
                .filter(Boolean)
                .join(' ')
                .trim();

        const oppPhone =
            String(od?.Phone ?? od?.phone ?? '').trim() || String(primary?.Phone ?? primary?.phone ?? '').trim();

        const oppEmail =
            String(od?.Email ?? od?.email ?? '').trim() || String(primary?.Email ?? primary?.email ?? '').trim();

        return { oppName, oppPhone, oppEmail, inst, sp };
    }, [opportunityDetails]);

    const { oppName, oppPhone, oppEmail, inst, sp } = glSidebar;

    const showOppName = !!oppName.trim();
    const showOppPhone = !!oppPhone.trim();
    const showOppEmail = !!oppEmail.trim();
    const showSpName = !!(sp?.Name ?? sp?.name ?? '').toString().trim();
    const showSpPhone = !!(sp?.Phone ?? '').toString().trim();
    const showSpEmail = !!(sp?.Email ?? sp?.email ?? '').toString().trim();

    return (
        <DashboardModal
            title={title}
            open={open && !!row}
            onClose={onClose}
            footer={null}
            width="90vw"
            theme={theme as 'light' | 'dark'}
            maskClosable={false}
            destroyOnClose
            zIndex={10080}
            className={`obd-gl-share-modal obd-theme-${theme}`}
            rootClassName={`obd-form-controls-modal-root obd-theme-${theme}`}
        >
            {!row ? null : phase === 'iframe' && iframeUrl ? (
                <div className="obd-gl-share__iframe-wrap">
                    <p className="obd-gl-share__iframe-hint">
                        GoodLeap loads in the frame below. If it stays blank, use <strong>Open in new tab</strong>.
                    </p>
                    <iframe
                        title="GoodLeap loan application"
                        src={iframeUrl}
                        className="obd-gl-share__iframe"
                        referrerPolicy="no-referrer"
                        allow="clipboard-write; fullscreen"
                    />
                    <div className="obd-gl-share__iframe-footer">
                        <Space>
                            <Button onClick={() => window.open(iframeUrl, '_blank', 'noopener,noreferrer')}>
                                Open in new tab
                            </Button>
                            <Button
                                type="primary"
                                onClick={() => {
                                    onClose();
                                }}
                            >
                                Close
                            </Button>
                        </Space>
                    </div>
                </div>
            ) : (
                <div className="obd-gl-share__body">
                    <div className="obd-gl-share__sidebar">
                        <section className="obd-gl-share__sidebar-section">
                            <h5 className="obd-gl-share__sidebar-title">Customer Information</h5>
                            {showOppName ? (
                                <div className="obd-gl-share__line">
                                    <span className="obd-gl-share__dot">
                                        <User size={14} weight="duotone" />
                                    </span>
                                    <span>{oppName}</span>
                                </div>
                            ) : null}
                            {showOppPhone ? (
                                <div className="obd-gl-share__line">
                                    <span className="obd-gl-share__dot">
                                        <Phone size={14} weight="duotone" />
                                    </span>
                                    <span>{oppPhone}</span>
                                </div>
                            ) : null}
                            {showOppEmail ? (
                                <div className="obd-gl-share__line">
                                    <span className="obd-gl-share__dot">
                                        <EnvelopeSimple size={14} weight="duotone" />
                                    </span>
                                    <span className="obd-gl-share__email-text">{oppEmail}</span>
                                </div>
                            ) : null}
                            {inst ? (
                                <div className="obd-gl-share__line">
                                    <span className="obd-gl-share__dot">
                                        <House size={14} weight="duotone" />
                                    </span>
                                    <span className="obd-gl-share__addr">
                                        {[
                                            inst.Street,
                                            inst.City,
                                            formatAddrStateSegment(String(inst.State ?? inst.stateCode ?? '')),
                                            inst.ZipCode ?? inst.zipCode,
                                        ]
                                            .filter(Boolean)
                                            .join(' ')}
                                    </span>
                                </div>
                            ) : null}
                        </section>

                        {sp ? (
                            <section className="obd-gl-share__sidebar-section obd-gl-share__sales">
                                <h5 className="obd-gl-share__sidebar-title">Sales Person</h5>
                                <div className="obd-gl-share__sales-head">
                                    {sp.Sales_Division ?? sp.sales_Division ?? 'Sales- Traditional'}
                                </div>
                                {showSpName ? (
                                    <div className="obd-gl-share__line">
                                        <span className="obd-gl-share__dot">
                                            <User size={14} weight="duotone" />
                                        </span>
                                        <span>{sp.Name ?? sp.name}</span>
                                    </div>
                                ) : null}
                                {showSpPhone ? (
                                    <div className="obd-gl-share__line">
                                        <span className="obd-gl-share__dot">
                                            <Phone size={14} weight="duotone" />
                                        </span>
                                        <span>{sp.Phone}</span>
                                    </div>
                                ) : null}
                                {showSpEmail ? (
                                    <div className="obd-gl-share__line">
                                        <span className="obd-gl-share__dot">
                                            <EnvelopeSimple size={14} weight="duotone" />
                                        </span>
                                        <span className="obd-gl-share__email-text">{sp.Email ?? sp.email}</span>
                                    </div>
                                ) : null}
                            </section>
                        ) : null}
                    </div>
                    <div className="obd-gl-share__main">
                        <div className="obd-gl-share__main-panel">
                            <h4 className="obd-gl-share__section-title">Loan share details</h4>
                            <div className="obd-gl-share__grid">
                            <Field label="Offer Id" required>
                                <Input
                                    value={offerId}
                                    onChange={(e) => setOfferId(e.target.value)}
                                    disabled
                                />
                            </Field>
                            <Field label="State" required>
                                <OBDSelect
                                    showSearch
                                    allowClear={false}
                                    optionFilterProp="label"
                                    style={{ width: '100%' }}
                                    value={stateVal || undefined}
                                    onChange={(v) => setStateVal(String(v))}
                                    placeholder="Select state"
                                    options={stateOptions}
                                    popupMatchSelectWidth={false}
                                    disabled
                                    popupClassName={`obd-select-dropdown obd-theme-${theme}`}
                                />
                            </Field>
                            <Field label="Amount Value" required>
                                <InputNumber
                                    className="obd-gl-share__input-number"
                                    style={{ width: '100%' }}
                                    prefix="$"
                                    min={0}
                                    max={1000000}
                                    precision={2}
                                    value={amountValue ?? undefined}
                                    onChange={(v) => setAmountValue(v === undefined || v === null ? null : v)}
                                    formatter={(v) => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={(display) => display!.replace(/\$\s?|(,*)/g, '')}
                                />
                            </Field>
                            <Field label="First Name" required>
                                <Input value={applicantFName} onChange={(e) => setApplicantFName(e.target.value)} disabled />
                            </Field>
                            <Field label="Last Name" required>
                                <Input value={applicantLName} onChange={(e) => setApplicantLName(e.target.value)} disabled />
                            </Field>
                            <Field label="Email" required>
                                <Input
                                    value={applicantEmail}
                                    onChange={(e) => {
                                        applicantEmailTouchedRef.current = true;
                                        setApplicantEmail(e.target.value);
                                    }}
                                />
                            </Field>
                            <Field label="Customer Preference" required>
                                <OBDSelect
                                    style={{ width: '100%' }}
                                    allowClear={false}
                                    value={sendEmail}
                                    onChange={(v) => setSendEmail(v as 'InPerson' | 'Email')}
                                    options={GOODLEAP_CUSTOMER_PREFERENCE_OPTIONS}
                                    placeholder="Select preference"
                                    optionLabelProp="label"
                                    popupClassName={`obd-select-dropdown obd-theme-${theme}`}
                                />
                            </Field>
                            <Field label="Submitting user First Name" required>
                                <Input
                                    value={subFName}
                                    onChange={(e) => {
                                        subTouchedRef.current.f = true;
                                        setSubFName(e.target.value);
                                    }}
                                />
                            </Field>
                            <Field label="Submitting user Last Name" required>
                                <Input
                                    value={subLName}
                                    onChange={(e) => {
                                        subTouchedRef.current.l = true;
                                        setSubLName(e.target.value);
                                    }}
                                />
                            </Field>
                            <Field label="Submitting user Email" required>
                                <Input
                                    value={subEmail}
                                    onChange={(e) => {
                                        subTouchedRef.current.e = true;
                                        setSubEmail(e.target.value);
                                    }}
                                />
                            </Field>
                            {showSystemDesign ? (
                                <>
                                    <Field label="First Year Production">
                                        <Input value={firstYearProd} onChange={(e) => setFirstYearProd(e.target.value)} disabled />
                                    </Field>
                                    <Field label="System Size" required>
                                        <Input value={systemSize} onChange={(e) => setSystemSize(e.target.value)} disabled />
                                    </Field>
                                </>
                            ) : null}
                            {showBattery ? (
                                <>
                                    <Field label="Battery Manufacturer">
                                        <Input value={batMfg} onChange={(e) => setBatMfg(e.target.value)} disabled />
                                    </Field>
                                    <Field label="Battery Model">
                                        <Input value={batModel} onChange={(e) => setBatModel(e.target.value)} disabled />
                                    </Field>
                                    <Field label="Battery Count">
                                        <Input value={batQty} onChange={(e) => setBatQty(e.target.value)} disabled />
                                    </Field>
                                    <Field label="Battery Adder Price Subtotal">
                                        <InputNumber
                                            className="obd-gl-share__input-number"
                                            style={{ width: '100%' }}
                                            prefix="$"
                                            min={0}
                                            precision={2}
                                            value={parseMoneyInput(batAdder) ?? undefined}
                                            disabled
                                            formatter={(v) => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                            parser={(display) => display!.replace(/\$\s?|(,*)/g, '')}
                                        />
                                    </Field>
                                    <Field label="Battery Price Subtotal">
                                        <InputNumber
                                            className="obd-gl-share__input-number"
                                            style={{ width: '100%' }}
                                            prefix="$"
                                            min={0}
                                            precision={2}
                                            value={parseMoneyInput(batPrice) ?? undefined}
                                            disabled
                                            formatter={(v) => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                            parser={(display) => display!.replace(/\$\s?|(,*)/g, '')}
                                        />
                                    </Field>
                                </>
                            ) : null}
                            </div>
                            {errText ? <p className="obd-gl-share__error">{errText}</p> : null}
                            <div className="obd-gl-share__footer">
                                <Button type="primary" onClick={() => void handleSubmit()} disabled={busy} loading={busy}>
                                    Submit
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </DashboardModal>
    );
};

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
    return (
        <div className="obd-gl-share__field">
            <label className="obd-gl-share__label">
                {label}
                {required ? <span className="obd-gl-share__req"> *</span> : null}
            </label>
            {children}
        </div>
    );
}
